import { getDateFnsLocale } from '@eventbrite/datetime-fns';
import { gettext } from '@eventbrite/i18n';
import { getCurrencySymbol } from '@eventbrite/intl';
import { format, isValid, parseISO } from 'date-fns';
import { formatInTimeZone, utcToZonedTime } from 'date-fns-tz';
import { isEmpty } from 'lodash';
import {
    AccordionSection,
    CalendarType,
    EventDate,
    EventStatus,
    TimeSlot,
} from '../../state/infiniteCalendarSeries';
import { hasMinPriceVariation } from '../TimedEntrySeries/TimeSlotContainer/utils';
import { longDateFormat } from '../TimedEntrySeries/types';
import { DATE_FORMATS } from './constants';

const eventStatusAlertMessages: Record<EventStatus, string> = {
    [EventStatus.canceled]: gettext('This event has been cancelled').toString(),
    [EventStatus.postponed]: gettext(
        'This event has been postponed',
    ).toString(),
    [EventStatus.salesEnded]: gettext(
        'Ticket sales for this event have ended',
    ).toString(),
};

export const getLocalMidnightDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    // Month is 0-indexed in JavaScript Date constructor, so we need to subtract 1.
    return new Date(year, month - 1, day);
};

export const getLocalFormattedDateOrEmpty = (date: string, locale: string) => {
    const yyyyDdMmRegex = /^\d{4}-\d{2}-\d{2}$/;
    // If the date is in the format YYYY-MM-DD, we need to get the local midnight date.
    // Otherwise if the date also has a time, we need to get the local date using exact date.
    const parsedDate = yyyyDdMmRegex.test(date)
        ? getLocalMidnightDate(date)
        : new Date(date);
    if (!isValid(parsedDate)) {
        return '';
    }
    return parsedDate.toLocaleDateString(locale, longDateFormat);
};

export const getMonthAndYearFromDate = (utcDate: string) => {
    const date = new Date(utcDate);
    return { month: date.getMonth(), year: date.getFullYear() };
};

export const isAccordionSectionOpen = (
    activeSection: AccordionSection | null,
    currentSection: AccordionSection,
): boolean => {
    return activeSection === currentSection;
};

export const isDateOnlyString = (isoDate: string): boolean => {
    return /^\d{4}-\d{2}-\d{2}$/.test(isoDate);
};

export const createInvalidDateResponse = () => ({
    date: new Date(NaN),
    format: () => '',
});

export const getValidDateFromIsoString = (isoDate: string) => {
    const parsedDate = parseISO(isoDate);
    if (!isValid(parsedDate)) {
        return null;
    }
    return parsedDate;
};

export const convertDateForTimezone = (
    parsedDate: Date,
    isoDate: string,
    timezone: string,
): Date => {
    if (isDateOnlyString(isoDate)) {
        return parsedDate;
    }
    try {
        const convertedDate = utcToZonedTime(parsedDate, timezone);
        return isValid(convertedDate) ? convertedDate : parsedDate;
    } catch {
        return parsedDate;
    }
};

export const createFormatFunction = (
    originalParsedDate: Date,
    dateObj: Date,
    isoDate: string,
    timezone: string,
) => {
    return (locale: string, dateFormat: string) => {
        if (isDateOnlyString(isoDate)) {
            return format(dateObj, dateFormat, {
                locale: getDateFnsLocale(locale),
            });
        } else {
            return formatInTimeZone(originalParsedDate, timezone, dateFormat, {
                locale: getDateFnsLocale(locale),
            });
        }
    };
};

export const getFormattedDate = ({
    isoDate,
    timezone,
}: {
    isoDate: string;
    timezone: string;
}) => {
    try {
        const parsedDate = getValidDateFromIsoString(isoDate);
        if (!parsedDate) {
            return createInvalidDateResponse();
        }

        const dateObj = convertDateForTimezone(parsedDate, isoDate, timezone);

        return {
            date: dateObj,
            format: createFormatFunction(
                parsedDate,
                dateObj,
                isoDate,
                timezone,
            ),
        };
    } catch {
        return createInvalidDateResponse();
    }
};

export const getDisplayDate = ({
    activeSection,
    calendarType,
    date,
    locale,
    timezone,
}: {
    activeSection?: AccordionSection | null;
    calendarType?: CalendarType | null;
    date: string;
    locale: string;
    timezone: string;
}): string => {
    if (activeSection === AccordionSection.date || isEmpty(date)) {
        return '';
    }
    switch (calendarType) {
        case CalendarType.compactWithSession:
            return getFormattedDate({
                isoDate: date,
                timezone,
            }).format(locale, DATE_FORMATS.SHORT_DATE_WITH_TIME);
        default:
            return getFormattedDate({
                isoDate: date,
                timezone,
            }).format(locale, DATE_FORMATS.SHORT_DATE);
    }
};

export const getUnavailableEventAlert = (eventStatus: EventStatus) => {
    return eventStatusAlertMessages[eventStatus] || '';
};

export const getCurrentTimezoneUnavailableAlert = (eventTimezone: string) =>
    gettext(
        "We couldn't detect your timezone. Times shown are in the %(eventTimezone)s timezone.",
        { eventTimezone },
    ).toString();

export const getPriceWithCurrencySymbol = (
    price: string | undefined,
    currency?: string,
): string => {
    if (!price) {
        return '';
    }
    const currencySymbol = getCurrencySymbol(currency);
    return `${currencySymbol}${price}`;
};

export const getAllSessionsMinPrice = (
    sessionData: Array<{ minPrice?: string }> | undefined,
): string | undefined => {
    if (
        !sessionData?.length ||
        hasMinPriceVariation(sessionData as TimeSlot[] | EventDate[])
    ) {
        return undefined;
    }
    return sessionData.find((slot) => slot.minPrice)?.minPrice;
};
